/**
 * The app's tiny inline-copy helper for the cloud-facing surfaces.
 *
 * Screens outside the app's vue-i18n catalogue — the account centre, the
 * partner and setup sheets, the hub-status pills — render their copy inline in
 * English; every string those screens add has to be readable in English and
 * Slovenian (the business side of Torik is Slovenian first). So instead of
 * threading a key through six locale files for a surface that is English inline
 * anyway, a string is written as a pair and picked here.
 *
 * Reads the locale store, so it stays reactive in a template: the current
 * locale is tracked on every render and a language switch re-renders the copy.
 */

import { useLocaleStore } from '../stores/locale';

/** Pick the Slovenian wording when the app runs in Slovenian, else English. */
export function tt(en: string, sl: string): string {
  return useLocaleStore().currentLocale === 'sl' ? sl : en;
}

/** The active locale, narrowed to the two languages this section speaks. */
export function consoleLocale(): 'en' | 'sl' {
  return useLocaleStore().currentLocale === 'sl' ? 'sl' : 'en';
}

// ── The API's own enum words ─────────────────────────────────────────────────

/**
 * A word the cloud sends as an enum (`OWNER`, `ACTIVE`, `MONTH`), read the way
 * a screen already words it. The English half is the word that reached the
 * screen before this mapper, and a value this build does not know — a new role,
 * a status from somewhere else — is returned capitalised exactly as the screen
 * worded it, never blank and never a guess.
 */
function apiWord(word: string | null | undefined, words: Record<string, [string, string]>): string {
  if (!word) return '';
  const pair = words[word.toUpperCase()];
  if (pair) return tt(pair[0], pair[1]);
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/** A subscription's status (`ACTIVE`, `TRIALING`, `PAST_DUE`, `UNPAID`, …). */
const SUBSCRIPTION_STATUS: Record<string, [string, string]> = {
  INCOMPLETE: ['Incomplete', 'Nedokončana'],
  TRIALING: ['Trialing', 'Poskusna'],
  ACTIVE: ['Active', 'Aktivna'],
  PAST_DUE: ['Past due', 'Zapadla'],
  UNPAID: ['Unpaid', 'Neplačana'],
  CANCELED: ['Canceled', 'Preklicana'],
};

export function subscriptionStatusLabel(status: string): string {
  return apiWord(status, SUBSCRIPTION_STATUS);
}

/** A plan's billing interval (`MONTH` / `YEAR`), as a price reads it. */
const BILLING_INTERVALS: Record<string, [string, string]> = {
  MONTH: ['Month', 'mesec'],
  YEAR: ['Year', 'leto'],
};

export function billingIntervalLabel(interval: string): string {
  return apiWord(interval, BILLING_INTERVALS);
}

/** A charge on the account ledger (`PAID` / `PENDING` / `FAILED` / `REFUNDED`). */
const LEDGER_STATUS: Record<string, [string, string]> = {
  PAID: ['Paid', 'Plačano'],
  PENDING: ['Pending', 'V čakanju'],
  FAILED: ['Failed', 'Neuspešno'],
  REFUNDED: ['Refunded', 'Povrnjeno'],
  // Charges the web posted from Stripe arrive with Stripe's own word; the
  // payments pill already counts it as paid (`status === 'SUCCEEDED'`), so it
  // reads as paid here too.
  SUCCEEDED: ['Succeeded', 'Uspešno'],
};

export function ledgerStatusLabel(status: string): string {
  return apiWord(status, LEDGER_STATUS);
}

/** A hardware order's status (`DRAFT` … `CANCELLED`). */
const ORDER_STATUS: Record<string, [string, string]> = {
  DRAFT: ['Draft', 'Osnutek'],
  PENDING: ['Pending', 'V čakanju'],
  PROCESSING: ['Processing', 'V obdelavi'],
  SHIPPED: ['Shipped', 'Poslano'],
  DELIVERED: ['Delivered', 'Dostavljeno'],
  CANCELLED: ['Cancelled', 'Preklicano'],
};

export function orderStatusLabel(status: string): string {
  return apiWord(status, ORDER_STATUS);
}

/** A hub's own state (`online` / `offline` / `updating`). */
const HUB_STATUS: Record<string, [string, string]> = {
  ONLINE: ['Online', 'Povezan'],
  OFFLINE: ['Offline', 'Nepovezan'],
  UPDATING: ['Updating', 'Posodabljanje'],
};

/** A server's status word, as the servers list words it. */
export function hubStatusLabel(status: string): string {
  return apiWord(status, HUB_STATUS);
}

/**
 * An integration's own state on the site card's status light (`connected` /
 * `disconnected` / `disabled`), read beside the integration's name
 * (`BACnet: disabled`).
 *
 * Same shape as `closingIssueLabel` above rather than `apiWord`: the English
 * half is the word the screen already painted — the hub's own lowercase word —
 * and `apiWord` capitalises (`online` -> `Online`), which would move it. A state
 * this build does not know (a fourth value from a newer hub) falls through to
 * the hub's own word, never blank.
 *
 * The Slovene halves are the words the integration pages already use for the
 * same three states (`views.integrationKNX.statusConnected` /
 * `statusDisconnected` / `statusDisabled`, the pill `IntegrationPageHeader`
 * paints for an integration).
 */
const INTEGRATION_STATES: Record<string, [string, string]> = {
  connected: ['connected', 'povezano'],
  disconnected: ['disconnected', 'prekinjeno'],
  disabled: ['disabled', 'onemogočeno'],
};

export function integrationStateLabel(state: string): string {
  const pair = INTEGRATION_STATES[state];
  return pair ? tt(pair[0], pair[1]) : state;
}

/**
 * The signed-in account's own role (`USER` / `PARTNER` / `SUPPORT` / `ADMIN`).
 *
 * This is `User.role`, which is a **different vocabulary** from the role a
 * person holds *inside an organization* (`OWNER` / `ADMIN` / `STAFF` /
 * `VIEWER`, `membershipRoleLabel` above) — `ADMIN` is the only word the two
 * tables share. Reading a membership role through this table (or this one
 * through that) would leave `USER` and `SUPPORT` in English on a Slovene
 * screen: "Vloga: User".
 */
const USER_ROLES: Record<string, [string, string]> = {
  USER: ['User', 'Uporabnik'],
  PARTNER: ['Partner', 'Partner'],
  SUPPORT: ['Support', 'Podpora'],
  ADMIN: ['Admin', 'Administrator'],
};

export function userRoleLabel(role: string): string {
  return apiWord(role, USER_ROLES);
}

/** The app's own spelling of an enum word: `SERVER_PLAN` → "Server Plan". */
function prettifyEnumWord(word: string): string {
  return word
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * What a ledger entry charges for (`APP_LICENSE` / `SERVER_PLAN` / …).
 *
 * Same idea as `closingIssueLabel`: the English is not this table's to spell —
 * the payments table has always shown the app's own prettifier ("Server Plan")
 * — so English keeps exactly that wording and only a Slovene screen looks the
 * word up. A type this build does not know falls through to the prettifier's
 * own wording, never blank.
 */
const LEDGER_TYPES_SL: Record<string, string> = {
  LISTING_FEE: 'Pristojbina za oglas',
  APP_LICENSE: 'Licenca storitve',
  SERVER_PLAN: 'Paket strežnika',
  CREDIT_PACK: 'Paket kreditov',
  ADJUSTMENT: 'Popravek',
};

export function ledgerTypeLabel(type: string): string {
  const pretty = prettifyEnumWord(String(type || ''));
  if (useLocaleStore().currentLocale !== 'sl') return pretty;
  return LEDGER_TYPES_SL[String(type || '').toUpperCase()] ?? pretty;
}

// ── The cloud's own plan codes ───────────────────────────────────────────────

/**
 * A plan code as words (`free` -> "Free", `pro-lite` -> "Pro Lite") — the rule
 * the hub's own licence panel uses (`SettingsGeneral.vue` `humaniseCode`, card
 * `t_ca1f68fc`) and the plan card on the web side shares (`t_cb6b184c`), so one
 * code cannot read three ways across the app.
 *
 * The brand prefix is deliberately NOT part of this: a surface composes it
 * itself (`${platformCompact} ${humanisePlanCode(code)}` -> "Torik Free"),
 * because "Torik Free" is the plan's *name* while the code is what the cloud
 * sends.
 */
export function humanisePlanCode(code: string): string {
  return code
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// ── The hub's own security alarm events ──────────────────────────────────────

/**
 * A security alarm-log entry's own event type (`ALARM_TRIGGERED`, `ARMED`, …).
 *
 * Same shape as `closingIssueLabel` above, not `apiWord`: the English half is the
 * word the log already painted — the hub's raw enum value, `ALARM_TRIGGERED` —
 * and `apiWord` would re-spell it (`Alarm_triggered`), moving the English render.
 * Only a Slovene screen looks the word up, and a value this build does not know
 * (a newer hub's event) falls through to the hub's own word, never blank.
 */
const SECURITY_ALARM_EVENTS_SL: Record<string, string> = {
  ARMED: 'Aktiviran',
  DISARMED: 'Deaktiviran',
  ALARM_TRIGGERED: 'Alarm sprožen',
  ALARM_CLEARED: 'Alarm preklican',
  SENSOR_BYPASS: 'Senzor obiden',
  SENSOR_TAMPER: 'Poseg v senzor',
  PANIC: 'Panika',
  DURESS_DISARM: 'Izklop pod prisilo',
  AUTO_ARM: 'Samodejno aktiviranje',
  AUTO_DISARM: 'Samodejna deaktivacija',
  MODE_CHANGED: 'Sprememba načina',
};

export function securityAlarmEventLabel(event: string): string {
  if (useLocaleStore().currentLocale !== 'sl') return event;
  return SECURITY_ALARM_EVENTS_SL[String(event || '').toUpperCase()] ?? event;
}
